/**
 * useCurrentPublishFingerprints.ts
 *
 * PLAT-10024 — recomputes the *current* staleness fingerprint for the clip
 * being viewed, so `DownloadsTab` can compare each record's stored fingerprint
 * against live state and flag outdated renders.
 *
 * Current-AR, sync, no S3 load: the per-AR slices (timeline / transcript /
 * captions / scenes) read the loaded clip variant's in-memory resources (from
 * the clip adapter's `currentVideo`); the per-language slices read the
 * `TranslationStore`. These are the identical inputs the fan-out hashes at
 * publish, so the two sides are apples-to-apples.
 *
 * The returned callback must be invoked in the render path of an `observer`
 * component — its store reads are what make staleness reactive to edits.
 */

import { selectKaraokeVariantForLanguage } from '@src/editor/models/karaoke/selectKaraokeVariant';
import { useStore } from '@src/stores-v2/StoreContext';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { computePublishFingerprints } from '@src/utility/publishFingerprints';

import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { PublishFingerprints } from '@src/types.ts/publishRecord';
import type { CompositionPhraseTranslation } from '@src/videoTranscriptionProcessor/translation/compositionTypes';

const logger = LogManager.getLogger('hook:useCurrentPublishFingerprints');

const EMPTY_COMPOSITION_PHRASES: ReadonlyMap<
  string,
  CompositionPhraseTranslation
> = new Map();

/**
 * Returns a function that computes the current fingerprint for a record's
 * language, or `null` when the loaded clip has no source to hash (nothing to
 * compare against — treat as not-outdated).
 *
 * The clip `adapter` (from `useClipPreviewRenderer`) is the sole source for the
 * per-AR slices; its `currentVideo` is the loaded variant. Pass it explicitly
 * — there is no global-store fallback, so the adapter-vs-root choice is never
 * silently made here. `null` while the adapter is still initializing.
 */
export function useCurrentPublishFingerprints(
  adapter: Partial<IVideoCreatorStore> | null,
): (language: string) => PublishFingerprints | null {
  const { translationStore } = useStore();

  // Per-AR inputs from the loaded variant (read here so an `observer` caller
  // re-runs when the clip's source/transcript/karaoke/scenes change).
  const current = adapter?.currentVideo;
  const videoSource = current?.videoSource;
  const transcriptElements = current?.transcriptionSnapshot?.elements ?? [];
  const karaokeData = current?.karaokeData ?? null;
  const karaokeDataByLanguage = current?.karaokeDataByLanguage;
  const scenes = current?.scenes ?? null;
  const originalLanguage = adapter?.story?.originalVideoLanguage ?? 'en';

  return (language: string): PublishFingerprints | null => {
    if (!videoSource) return null;
    try {
      const isOriginal =
        isOriginalLang(language) ||
        (!!originalLanguage && language === originalLanguage);
      // PLAT-10788 — select the per-language variant (lockstep with the stored
      // side in `PublishFanoutService.computeCellFingerprints`), so the captions
      // slice compares apples-to-apples per language.
      const cellKaraokeData =
        selectKaraokeVariantForLanguage(karaokeDataByLanguage, {
          isOriginal,
          langCode: language,
        }) ??
        // try to fallback to the real base, not whatever is in karaokeData
        // (can be another translation)
        selectKaraokeVariantForLanguage(karaokeDataByLanguage, {
          isOriginal: true,
          langCode: originalLanguage,
        }) ??
        karaokeData ??
        null;
      return computePublishFingerprints({
        videoSource: videoSource as Record<string, unknown>,
        transcriptElements,
        karaokeData: cellKaraokeData,
        translationPhrases: isOriginal
          ? null
          : (translationStore.getTranslation(language)?.phrases ?? null),
        compositionPhrases: isOriginal
          ? EMPTY_COMPOSITION_PHRASES
          : translationStore.getCompositionPhrasesByNormalizedSource(language),
        scenes,
      });
    } catch (err) {
      logger.warn('Failed to compute current fingerprint', {
        language,
        error: extractErrorInfo(err),
      });
      return null;
    }
  };
}
